import { useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import { Plus, Calendar, Edit2, Trash2, ArrowUpDown, Check } from 'lucide-react';
import { format } from 'date-fns';
import { toast } from 'sonner';
import { createMeasurement, updateMeasurement, deleteMeasurement, getBodyMeasurementFields } from '@/lib/api';

const DEFAULT_BODY_FIELDS = [
  { field_key: 'neck_circumference', field_label: 'Neck Circumference', field_type: 'number', display_order: 1, is_active: true },
  { field_key: 'across_front', field_label: 'Across Front', field_type: 'number', display_order: 2, is_active: true },
  { field_key: 'chest_circumference', field_label: 'Chest Circumference', field_type: 'number', display_order: 3, is_active: true },
  { field_key: 'upper_waist_circumference', field_label: 'Upper Waist', field_type: 'number', display_order: 4, is_active: true },
  { field_key: 'belly_circumference', field_label: 'Belly', field_type: 'number', display_order: 5, is_active: true },
  { field_key: 'upper_seat_hip_circumference', field_label: 'Upper Seat/Hip', field_type: 'number', display_order: 6, is_active: true },
  { field_key: 'front_length_upper', field_label: 'Front Length', field_type: 'number', display_order: 7, is_active: true },
  { field_key: 'shoulder', field_label: 'Shoulder', field_type: 'number', display_order: 8, is_active: true },
  { field_key: 'across_back', field_label: 'Across Back', field_type: 'number', display_order: 9, is_active: true },
  { field_key: 'back_length_upper', field_label: 'Back Length', field_type: 'number', display_order: 10, is_active: true },
  { field_key: 'biceps_circumference', field_label: 'Biceps', field_type: 'number', display_order: 11, is_active: true },
  { field_key: 'elbow_circumference', field_label: 'Elbow', field_type: 'number', display_order: 12, is_active: true },
  { field_key: 'sleeve_length', field_label: 'Sleeve Length', field_type: 'number', display_order: 13, is_active: true },
  { field_key: 'left_wrist_circumference', field_label: 'Left Wrist', field_type: 'number', display_order: 14, is_active: true },
  { field_key: 'left_wrist_note', field_label: 'Left Wrist Note', field_type: 'text', display_order: 15, is_active: true },
  { field_key: 'right_wrist_circumference', field_label: 'Right Wrist', field_type: 'number', display_order: 16, is_active: true },
  { field_key: 'right_wrist_note', field_label: 'Right Wrist Note', field_type: 'text', display_order: 17, is_active: true },
  { field_key: 'waistcoat_front_neck_depth', field_label: 'Waistcoat Front Neck Depth', field_type: 'number', display_order: 18, is_active: true },
  { field_key: 'waistcoat_front_length', field_label: 'Waistcoat Front Length', field_type: 'number', display_order: 19, is_active: true },
  { field_key: 'waistcoat_back_length', field_label: 'Waistcoat Back Length', field_type: 'number', display_order: 20, is_active: true },
  { field_key: 'lower_waist_circumference', field_label: 'Lower Waist', field_type: 'number', display_order: 21, is_active: true },
  { field_key: 'lower_seat_hip_circumference', field_label: 'Lower Seat/Hip', field_type: 'number', display_order: 22, is_active: true },
  { field_key: 'front_rise', field_label: 'Front Rise', field_type: 'number', display_order: 23, is_active: true },
  { field_key: 'u_measurement', field_label: 'U-Measure', field_type: 'number', display_order: 24, is_active: true },
  { field_key: 'thigh_circumference', field_label: 'Thigh', field_type: 'number', display_order: 25, is_active: true },
  { field_key: 'knee_circumference', field_label: 'Knee', field_type: 'number', display_order: 26, is_active: true },
  { field_key: 'outseam', field_label: 'Outseam', field_type: 'number', display_order: 27, is_active: true },
];

const KNOWN_TEXT_KEYS = new Set(['left_wrist_note', 'right_wrist_note']);
const KNOWN_NUMERIC_KEYS = new Set(DEFAULT_BODY_FIELDS.filter((field) => field.field_type === 'number').map((field) => field.field_key));

const BodyMeasurementsTab = ({ customerId, customer, measurements, onRefresh }) => {
  const [compareMode, setCompareMode] = useState(false);
  const [selectedMeasurementIds, setSelectedMeasurementIds] = useState([]);
  const [newMeasurementDialogOpen, setNewMeasurementDialogOpen] = useState(false);
  const [editMeasurementDialogOpen, setEditMeasurementDialogOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [measurementToEdit, setMeasurementToEdit] = useState(null);
  const [measurementToDelete, setMeasurementToDelete] = useState(null);
  const [saving, setSaving] = useState(false);
  const [fieldDefinitions, setFieldDefinitions] = useState(DEFAULT_BODY_FIELDS);
  const [fieldDefinitionsLoading, setFieldDefinitionsLoading] = useState(false);

  const activeFieldDefinitions = useMemo(
    () => (fieldDefinitions || []).filter((field) => field.is_active !== false).sort((a, b) => (a.display_order || 0) - (b.display_order || 0)),
    [fieldDefinitions]
  );

  const buildEmptyMeasurementForm = () => ({
    measurement_date: new Date().toISOString().split('T')[0],
    unit: 'in',
    notes: '',
    measurement_text_overrides: {},
    ...Object.fromEntries(activeFieldDefinitions.map((field) => [field.field_key, ''])),
  });
  
  const [measurementForm, setMeasurementForm] = useState({
    measurement_date: new Date().toISOString().split('T')[0],
    unit: 'in',
    notes: '',
    measurement_text_overrides: {},
    ...Object.fromEntries(DEFAULT_BODY_FIELDS.map(field => [field.field_key, '']))
  });

  useEffect(() => {
    const fetchBodyFieldDefinitions = async () => {
      setFieldDefinitionsLoading(true);
      try {
        const response = await getBodyMeasurementFields();
        const apiFields = (response.data || []).map((field) => ({
          field_key: field.field_key,
          field_label: field.field_label,
          field_type: field.field_type || 'number',
          display_order: field.display_order || 0,
          is_active: field.is_active !== false,
        }));

        if (apiFields.length > 0) {
          setFieldDefinitions(apiFields);
        }
      } catch (_error) {
        // Safe fallback to defaults
        setFieldDefinitions(DEFAULT_BODY_FIELDS);
      } finally {
        setFieldDefinitionsLoading(false);
      }
    };

    fetchBodyFieldDefinitions();
  }, []);

  const parseLeadingNumber = (value) => {
    const text = String(value ?? '').trim();
    if (!text) return null;
    const match = text.match(/-?\d+(\.\d+)?/);
    if (!match) return null;
    const parsed = parseFloat(match[0]);
    return Number.isNaN(parsed) ? null : parsed;
  };

  const buildPayloadFromMeasurementForm = () => {
    const cleanedPayload = {
      customer_id: customerId,
      measurement_date: measurementForm.measurement_date,
      unit: 'inches',
      notes: measurementForm.notes || null,
      measurement_text_overrides: {},
    };

    activeFieldDefinitions.forEach((field) => {
      const key = field.field_key;
      const value = measurementForm[key];
      const trimmedValue = value === null || value === undefined ? '' : String(value).trim();
      const isEmpty = trimmedValue === '';
      const fieldType = field.field_type || 'number';

      if (fieldType === 'number') {
        const numericValue = isEmpty ? null : parseFloat(trimmedValue);
        const parsedValue = Number.isNaN(numericValue) ? null : numericValue;
        if (KNOWN_NUMERIC_KEYS.has(key)) {
          cleanedPayload[key] = parsedValue;
        } else if (!isEmpty) {
          cleanedPayload.measurement_text_overrides[key] = trimmedValue;
        }
        return;
      }

      if (KNOWN_TEXT_KEYS.has(key)) {
        cleanedPayload[key] = isEmpty ? null : trimmedValue;
        return;
      }

      if (KNOWN_NUMERIC_KEYS.has(key)) {
        cleanedPayload[key] = parseLeadingNumber(trimmedValue);
      }
      if (!isEmpty) {
        cleanedPayload.measurement_text_overrides[key] = trimmedValue;
      }
    });

    if (Object.keys(cleanedPayload.measurement_text_overrides).length === 0) {
      cleanedPayload.measurement_text_overrides = {};
    }

    return cleanedPayload;
  };

  const getMeasurementDisplayValue = (measurement, field) => {
    const key = field.field_key;
    const baseValue = measurement?.[key];
    const textOverrides = measurement?.measurement_text_overrides || {};

    if (key === 'left_wrist_circumference') {
      const wristNote = measurement?.left_wrist_note;
      if (wristNote && baseValue !== null && baseValue !== undefined && baseValue !== '') {
        return `${baseValue} (${wristNote})`;
      }
    }
    if (key === 'right_wrist_circumference') {
      const wristNote = measurement?.right_wrist_note;
      if (wristNote && baseValue !== null && baseValue !== undefined && baseValue !== '') {
        return `${baseValue} (${wristNote})`;
      }
    }

    if ((field.field_type || 'number') === 'text') {
      return textOverrides[key] || baseValue || '';
    }

    return baseValue;
  };

  const handleOpenNewMeasurement = () => {
    console.log('handleOpenNewMeasurement called');
    console.log('measurements:', measurements);
    // Pre-fill with latest measurements for convenience, or use defaults for initial measurement
    const latestMeas = measurements[0];
    if (latestMeas) {
      console.log('Using latest measurements');
      const formData = buildEmptyMeasurementForm();
      formData.measurement_date = new Date().toISOString().split('T')[0];
      formData.unit = latestMeas.unit || 'in';
      formData.notes = '';
      formData.measurement_text_overrides = latestMeas.measurement_text_overrides || {};
      activeFieldDefinitions.forEach((field) => {
        const key = field.field_key;
        const overrideValue = latestMeas.measurement_text_overrides?.[key];
        formData[key] = overrideValue ?? latestMeas[key] ?? '';
      });
      setMeasurementForm(formData);
    } else {
      // No existing measurements, use default form
      console.log('No existing measurements, using defaults');
      setMeasurementForm(buildEmptyMeasurementForm());
    }
    console.log('Setting newMeasurementDialogOpen to true');
    setNewMeasurementDialogOpen(true);
    console.log('newMeasurementDialogOpen should be true now');
  };

  const handleSaveNewMeasurement = async () => {
    try {
      setSaving(true);
      
      const cleanedPayload = buildPayloadFromMeasurementForm();
      
      console.log('Sending measurement payload:', cleanedPayload);
      
      await createMeasurement(cleanedPayload);
      toast.success('Body measurements added');
      setNewMeasurementDialogOpen(false);
      onRefresh();
    } catch (error) {
      console.error('Error saving measurement:', error);
      console.error('Error response:', error.response);
      toast.error(error.response?.data?.detail || 'Failed to add measurements');
    } finally {
      setSaving(false);
    }
  };

  const handleOpenEditMeasurement = (measurement) => {
    setMeasurementToEdit(measurement);
    
    const formData = buildEmptyMeasurementForm();
    formData.measurement_date = measurement.measurement_date.split('T')[0];
    formData.unit = 'in';
    formData.notes = measurement.notes || '';
    formData.measurement_text_overrides = measurement.measurement_text_overrides || {};
    activeFieldDefinitions.forEach((field) => {
      const key = field.field_key;
      const overrideValue = measurement.measurement_text_overrides?.[key];
      formData[key] = overrideValue ?? measurement[key] ?? '';
    });
    setMeasurementForm(formData);
    setEditMeasurementDialogOpen(true);
  };

  const handleSaveEditMeasurement = async () => {
    if (!measurementToEdit) return;
    
    try {
      setSaving(true);
      
      const cleanedPayload = buildPayloadFromMeasurementForm();
      
      console.log('Update payload:', JSON.stringify(cleanedPayload, null, 2));
      
      await updateMeasurement(measurementToEdit.measurement_id, cleanedPayload);
      
      toast.success('Measurements updated');
      setEditMeasurementDialogOpen(false);
      setMeasurementToEdit(null);
      onRefresh();
    } catch (error) {
      console.error('Update error:', error);
      console.error('Error response:', error.response?.data);
      toast.error(error.response?.data?.detail || 'Failed to update measurements');
    } finally {
      setSaving(false);
    }
  };

  const handleOpenDeleteConfirm = (measurement) => {
    setMeasurementToDelete(measurement);
    setDeleteConfirmOpen(true);
  };

  const handleDeleteMeasurement = async () => {
    if (!measurementToDelete) return;
    
    try {
      setSaving(true);
      await deleteMeasurement(measurementToDelete.measurement_id);
      
      toast.success('Body measurement deleted');
      setDeleteConfirmOpen(false);
      setMeasurementToDelete(null);
      onRefresh();
    } catch (error) {
      console.error('Delete error:', error);
      toast.error('Failed to delete measurement');
    } finally {
      setSaving(false);
    }
  };

  const toggleMeasurementSelection = (measurementId) => {
    if (selectedMeasurementIds.includes(measurementId)) {
      setSelectedMeasurementIds(selectedMeasurementIds.filter(id => id !== measurementId));
    } else {
      if (selectedMeasurementIds.length >= 3) {
        toast.error('Maximum 3 measurements can be compared');
        return;
      }
      setSelectedMeasurementIds([...selectedMeasurementIds, measurementId]);
    }
  };

  const getValueDiff = (currentVal, prevVal) => {
    if (!prevVal || !currentVal) return null;
    const diff = parseFloat(currentVal) - parseFloat(prevVal);
    if (isNaN(diff) || diff === 0) return null;
    return diff > 0 ? `+${diff.toFixed(2)}` : diff.toFixed(2);
  };

  if (!measurements || measurements.length === 0) {
    return (
      <>
        <Card>
          <CardContent className="py-12">
            <div className="text-center">
              <Calendar className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
              <p className="text-lg font-medium mb-2">No Body Measurements Yet</p>
              <p className="text-sm text-muted-foreground mb-4">
                Start by recording initial body measurements for {customer?.customer_name || customer?.name}
              </p>
              <Button onClick={handleOpenNewMeasurement}>
                <Plus className="w-4 h-4 mr-2" />
                Add Initial Measurements
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* New Measurement Dialog - Always render */}
        <Dialog open={newMeasurementDialogOpen} onOpenChange={setNewMeasurementDialogOpen}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Add New Body Measurements</DialogTitle>
              <DialogDescription>
                Record new body measurements for {customer?.customer_name || customer?.name}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Measurement Date</Label>
                  <Input
                    type="date"
                    value={measurementForm.measurement_date}
                    onChange={(e) => setMeasurementForm({ ...measurementForm, measurement_date: e.target.value })}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Notes (Optional)</Label>
                  <Input
                    placeholder="e.g., Initial fitting"
                    value={measurementForm.notes}
                    onChange={(e) => setMeasurementForm({ ...measurementForm, notes: e.target.value })}
                    className="mt-1"
                  />
                </div>
              </div>

              <div className="border-t pt-4">
                <h3 className="font-semibold mb-3">Measurements (in)</h3>
                <div className="grid grid-cols-3 gap-4">
                  {activeFieldDefinitions.map(field => (
                    <div key={field.field_key}>
                      <Label className="text-xs">{field.field_label}</Label>
                      <Input
                        type={field.field_type === 'text' ? 'text' : 'number'}
                        step={field.field_type === 'text' ? undefined : '0.1'}
                        value={measurementForm[field.field_key] ?? ''}
                        onChange={(e) => setMeasurementForm({ ...measurementForm, [field.field_key]: e.target.value })}
                        className="mt-1"
                        data-testid={`body-measurement-input-${field.field_key}`}
                      />
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setNewMeasurementDialogOpen(false)} disabled={saving}>
                Cancel
              </Button>
              <Button onClick={handleSaveNewMeasurement} disabled={saving}>
                {saving ? 'Saving...' : 'Save Measurements'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </>
    );
  }

  // Display measurements based on compare mode
  const displayMeasurements = compareMode && selectedMeasurementIds.length > 0
    ? selectedMeasurementIds.map(id => measurements.find(m => m.measurement_id === id)).filter(Boolean)
    : measurements.slice(0, 3);
  const unit = measurements[0]?.unit === 'inches' ? 'in' : measurements[0]?.unit || 'in';

  return (
    <div className="space-y-4">
      {/* Action Bar */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold">Body Measurements</h2>
          <p className="text-sm text-muted-foreground">
            {measurements.length} measurement set{measurements.length !== 1 ? 's' : ''} recorded
            {fieldDefinitionsLoading ? ' • loading field definitions...' : ''}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {measurements.length > 1 && (
            <Button
              variant={compareMode ? "default" : "outline"}
              size="sm"
              onClick={() => {
                setCompareMode(!compareMode);
                if (!compareMode && measurements.length >= 2) {
                  // Auto-select first 2 when entering compare mode
                  setSelectedMeasurementIds([
                    measurements[0].measurement_id,
                    measurements[1].measurement_id
                  ]);
                }
              }}
            >
              <ArrowUpDown className="w-4 h-4 mr-2" />
              {compareMode ? 'Exit Compare' : 'Compare'}
            </Button>
          )}
          <Button onClick={handleOpenNewMeasurement}>
            <Plus className="w-4 h-4 mr-2" />
            Add New Measurement Set
          </Button>
        </div>
      </div>

      {/* Compare Mode: Measurement Selector */}
      {compareMode && (
        <Card className="bg-muted/50">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-3">
              <span className="text-sm font-medium">Select measurements to compare:</span>
              <Badge variant="secondary">{selectedMeasurementIds.length} selected (max 3)</Badge>
            </div>
            <div className="flex flex-wrap gap-2">
              {measurements.map((meas, idx) => {
                const isSelected = selectedMeasurementIds.includes(meas.measurement_id);
                return (
                  <Button
                    key={meas.measurement_id}
                    variant={isSelected ? "default" : "outline"}
                    size="sm"
                    onClick={() => toggleMeasurementSelection(meas.measurement_id)}
                    className="gap-2"
                  >
                    {isSelected && <Check className="w-3 h-3" />}
                    {idx === 0 ? 'Latest' : format(new Date(meas.measurement_date), 'MMM d, yyyy')}
                  </Button>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Measurements Table */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 border-b sticky top-0">
                <tr>
                  <th className="px-4 py-3 text-left font-semibold text-xs uppercase tracking-wider w-64">
                    Measurement
                  </th>
                  {displayMeasurements.map((measurement, idx) => (
                    <th 
                      key={measurement.measurement_id}
                      className={`px-4 py-3 text-center font-semibold text-xs uppercase tracking-wider min-w-[150px] ${
                        idx === 0 ? 'bg-blue-50/80' : idx === 1 ? 'bg-green-50/80' : 'bg-gray-50/80'
                      }`}
                    >
                      <div className="flex flex-col items-center gap-1">
                        <div className="flex items-center gap-2">
                          {idx === 0 && <Badge variant="default" className="text-[10px] px-1.5 py-0">Latest</Badge>}
                          <span>{format(new Date(measurement.measurement_date), 'MMM d, yyyy')}</span>
                        </div>
                        <div className="flex items-center gap-1 mt-1">
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className="h-6 text-xs"
                            onClick={() => handleOpenEditMeasurement(measurement)}
                          >
                            <Edit2 className="w-3 h-3 mr-1" />
                            Edit
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className="h-6 text-xs text-destructive"
                            onClick={() => handleOpenDeleteConfirm(measurement)}
                            data-testid={`body-measurement-delete-button-${measurement.measurement_id}`}
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y">
                {activeFieldDefinitions.map((field, fieldIdx) => (
                  <tr key={field.field_key} className={fieldIdx % 2 === 0 ? 'bg-white' : 'bg-gray-50/30'}>
                    <td className="px-4 py-3 font-medium">
                      {field.field_label}
                    </td>
                    {displayMeasurements.map((measurement, idx) => {
                      const rawValue = measurement[field.field_key];
                      const displayValue = getMeasurementDisplayValue(measurement, field);
                      const prevMeas = idx < displayMeasurements.length - 1 ? displayMeasurements[idx + 1] : null;
                      const prevValue = prevMeas?.[field.field_key];
                      const diff = field.field_type === 'number' ? getValueDiff(rawValue, prevValue) : null;

                      return (
                        <td
                          key={measurement.measurement_id}
                          className={`px-4 py-3 text-center ${
                            idx === 0 ? 'bg-blue-50/30' : idx === 1 ? 'bg-green-50/30' : 'bg-gray-50/30'
                          }`}
                        >
                          <div className="flex flex-col items-center gap-0.5">
                            <span className="font-mono font-medium">
                              {displayValue !== null && displayValue !== undefined && displayValue !== ''
                                ? `${displayValue}${field.field_type === 'number' ? ` ${unit}` : ''}`
                                : '—'}
                            </span>
                            {diff && compareMode && (
                              <span className={`text-xs font-medium ${
                                diff.startsWith('+') ? 'text-green-600' : 'text-red-600'
                              }`}>
                                {diff.startsWith('+') ? '↑' : '↓'} {diff}
                              </span>
                            )}
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Measurement History */}
      {measurements.length > 3 && !compareMode && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Older Measurements</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {measurements.slice(3).map((measurement) => (
                <div 
                  key={measurement.measurement_id}
                  className="flex items-center justify-between p-3 border rounded-lg hover:bg-muted/50"
                >
                  <div className="flex items-center gap-3">
                    <Calendar className="w-4 h-4 text-muted-foreground" />
                    <span className="font-medium">
                      {format(new Date(measurement.measurement_date), 'MMMM d, yyyy')}
                    </span>
                    {measurement.notes && (
                      <span className="text-sm text-muted-foreground">
                        — {measurement.notes}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-1">
                    <Button variant="ghost" size="sm">View</Button>
                    <Button variant="ghost" size="sm">Edit</Button>
                    <Button variant="ghost" size="sm" className="text-destructive">
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* New Measurement Dialog */}
      <Dialog open={newMeasurementDialogOpen} onOpenChange={setNewMeasurementDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add New Body Measurements</DialogTitle>
            <DialogDescription>
              Record new body measurements for {customer?.customer_name || customer?.name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Measurement Date</Label>
                <Input
                  type="date"
                  value={measurementForm.measurement_date}
                  onChange={(e) => setMeasurementForm({ ...measurementForm, measurement_date: e.target.value })}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Notes (Optional)</Label>
                <Input
                  placeholder="e.g., Initial fitting"
                  value={measurementForm.notes}
                  onChange={(e) => setMeasurementForm({ ...measurementForm, notes: e.target.value })}
                  className="mt-1"
                />
              </div>
            </div>

            <div className="border-t pt-4">
              <h3 className="font-semibold mb-3">Measurements ({unit})</h3>
              <div className="grid grid-cols-3 gap-4">
                {activeFieldDefinitions.map(field => (
                  <div key={field.field_key}>
                    <Label className="text-xs">{field.field_label}</Label>
                    <Input
                      type={field.field_type === 'text' ? 'text' : 'number'}
                      step={field.field_type === 'text' ? undefined : '0.25'}
                      placeholder={field.field_type === 'text' ? 'Enter text' : '0.00'}
                      value={measurementForm[field.field_key] ?? ''}
                      onChange={(e) => setMeasurementForm({
                        ...measurementForm,
                        [field.field_key]: e.target.value
                      })}
                      className="mt-1 h-9"
                      data-testid={`body-measurement-input-${field.field_key}`}
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setNewMeasurementDialogOpen(false)}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button onClick={handleSaveNewMeasurement} disabled={saving}>
              {saving ? 'Saving...' : 'Save Measurements'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Measurement Dialog */}
      <Dialog open={editMeasurementDialogOpen} onOpenChange={setEditMeasurementDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Body Measurements</DialogTitle>
            <DialogDescription>
              Update body measurements for {customer?.customer_name || customer?.name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Measurement Date</Label>
                <Input
                  type="date"
                  value={measurementForm.measurement_date}
                  onChange={(e) => setMeasurementForm({ ...measurementForm, measurement_date: e.target.value })}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Notes (Optional)</Label>
                <Input
                  placeholder="e.g., Initial fitting"
                  value={measurementForm.notes}
                  onChange={(e) => setMeasurementForm({ ...measurementForm, notes: e.target.value })}
                  className="mt-1"
                />
              </div>
            </div>

            <div className="border-t pt-4">
              <h3 className="font-semibold mb-3">Measurements ({unit})</h3>
              <div className="grid grid-cols-3 gap-4">
                {activeFieldDefinitions.map(field => (
                  <div key={field.field_key}>
                    <Label className="text-xs">{field.field_label}</Label>
                    <Input
                      type={field.field_type === 'text' ? 'text' : 'number'}
                      step={field.field_type === 'text' ? undefined : '0.25'}
                      placeholder={field.field_type === 'text' ? 'Enter text' : '0.00'}
                      value={measurementForm[field.field_key] ?? ''}
                      onChange={(e) => setMeasurementForm({
                        ...measurementForm,
                        [field.field_key]: e.target.value
                      })}
                      className="mt-1 h-9"
                      data-testid={`body-measurement-input-${field.field_key}`}
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setEditMeasurementDialogOpen(false)}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button onClick={handleSaveEditMeasurement} disabled={saving}>
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Delete Body Measurement</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this measurement set? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          {measurementToDelete && (
            <div className="py-4">
              <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <Calendar className="w-5 h-5 text-destructive" />
                  <span className="font-semibold">
                    {format(new Date(measurementToDelete.measurement_date), 'MMMM d, yyyy')}
                  </span>
                </div>
                {measurementToDelete.notes && (
                  <p className="text-sm mt-2">Notes: {measurementToDelete.notes}</p>
                )}
              </div>
              {measurements.length === 1 && (
                <p className="text-sm text-destructive mt-4">
                  ⚠️ This is the last body measurement for this client.
                </p>
              )}
            </div>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeleteConfirmOpen(false)}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteMeasurement}
              disabled={saving}
            >
              {saving ? 'Deleting...' : 'Delete Measurement'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BodyMeasurementsTab;
